"use client";

import { PictureAsset } from "@/components/common/picture-asset";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CalendarPlusIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReactNode, useState } from "react";
import { PracticeExamAddToCalendarDialog } from "./practice-exam-add-to-calendar";
import {
  getPracticeExamInfo,
  PracticeExamInfo,
} from "@/service/exams/practice-exam-info";

// Grafiki lewej kolumny dialogu (bez rozszerzenia — PictureAsset dokleja .avif/.png):
// pionowe tło 482x840 i wycinka postaci z chipami (PNG z kanałem alfa) na wierzchu.
const DIALOG_BACKGROUND =
  "https://cdn.remedium.md/image/common/2026/07/30/10/tlo2-png-6a6b11e85eb86";
const DIALOG_PERSON =
  "https://cdn.remedium.md/image/common/2026/07/30/10/popup-png-6a6b0d84cadd6";

const ListItem = ({ icon, text }: { icon: string; text: string }) => {
  return (
    <div className="flex items-start gap-2">
      <span className="text-muted-foreground shrink-0 text-sm leading-5">
        {icon}
      </span>
      <span className="text-sm leading-5 tracking-[-0.14px] text-zinc-700">
        {text}
      </span>
    </div>
  );
};

const Content = ({
  info,
  onOpen,
  onDone,
}: {
  info: PracticeExamInfo;
  onOpen?: () => void;
  onDone?: () => void;
}) => {
  const { startTime, endTime } = info;

  return (
    <div className="flex flex-1 flex-col justify-between gap-8 p-6 md:p-8">
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-semibold tracking-[-0.36px] text-black">
          Potwierdzamy Twój udział w próbnym LEK&#8209;u
        </h2>
        <div className="flex flex-col gap-2">
          <ListItem
            icon="🔔"
            text="Przypomnienia o rundach będą wysyłane automatycznie."
          />
          <ListItem
            icon="🤝"
            text="Twórz grupy rankingowe i rywalizuj ze znajomymi."
          />
          <ListItem
            icon="⏰"
            // godziny z danych rundy, nie na sztywno jak w makiecie
            text={`W dniu egzaminu zaloguj się i rozpocznij test między ${startTime} a ${endTime}.`}
          />
          <ListItem
            icon="🏁"
            text="Informacja o wynikach pojawi się po zakończeniu każdej rundy."
          />
        </div>
      </div>

      {/* Bez „Stwórz grupę" — grupy zakłada się na stronie próbnego LEK-u (kontrolki rankingu),
          ten dialog tylko potwierdza udział. Kalendarz jest opcjonalny, więc obok niego stoi
          wyjście — bez niego dialog wyglądał, jakby zapis dopinało dopiero dodanie wydarzenia. */}
      <div className="flex flex-col gap-2.5">
        <Button variant="default" onClick={onOpen}>
          <CalendarPlusIcon /> Dodaj do kalendarza
        </Button>
        <Button variant="outline" onClick={onDone}>
          Gotowe
        </Button>
      </div>
    </div>
  );
};

/**
 * Dialog potwierdzenia udziału w próbnym LEK-u (+ wybór kalendarza).
 *
 * Dwa tryby: z `children` otwiera go własny trigger (wejście informacyjne), a z `open`/`onOpenChange`
 * steruje nim rodzic — banery otwierają go dopiero po udanym zapisie, więc nie może polegać na
 * kliknięciu w trigger.
 *
 * `onClose` woła się raz, na wyjściu z całego flow (potwierdzenie albo dialog kalendarza) — banery
 * odświeżają na nim dane. Przejście „potwierdzenie → kalendarz" zamyka pierwszy dialog, ale flow
 * trwa, więc tam `onClose` nie leci; inaczej odświeżenie zdejmowałoby baner razem z otwartym
 * popupem kalendarza.
 */
export const PracticeExamDialog = ({
  children,
  practiceExamStatus,
  open,
  onOpenChange,
  onClose,
}: {
  children?: ReactNode;
  practiceExamStatus: ExamPracticeExamStatusVM;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}) => {
  const [isAddToCalendarOpen, setIsAddToCalendarOpen] = useState(false);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);

  const isControlled = undefined !== open;
  const isOpen = isControlled ? open : uncontrolledOpen;

  const info = getPracticeExamInfo(practiceExamStatus);

  if (!info || !practiceExamStatus.startDate) return null;

  const setOpen = (next: boolean) => {
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  // zamknięcie gestem (krzyżyk / tło / Esc) albo przyciskiem „Gotowe" kończy flow
  const handleOpenChange = (next: boolean) => {
    setOpen(next);

    if (!next) onClose?.();
  };

  const openCalendar = () => {
    setIsAddToCalendarOpen(true);
    setOpen(false);
  };

  const handleCalendarOpenChange = (next: boolean) => {
    setIsAddToCalendarOpen(next);

    if (!next) onClose?.();
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {children && <DialogTrigger>{children}</DialogTrigger>}
        <DialogContent className="w-full md:max-w-[654px]">
          <div className="flex items-stretch">
            {/* kolumna z grafiką ma w projekcie 241 px */}
            <div className="relative hidden w-[241px] shrink-0 overflow-hidden rounded-l-[8px] lg:block">
              {/* tło dialogu: pionowy kadr 482x840 (proporcje kolumny 241x420), więc bez obracania
                  i bez nakładki — plik jest już w docelowym kolorze */}
              <PictureAsset
                source={DIALOG_BACKGROUND}
                ext="png"
                alt=""
                wrapperClassName="pointer-events-none absolute inset-0"
                className="h-full w-full object-cover"
              />
              {/* postać z chipami — PNG z kanałem alfa, więc tło z banera zostaje widoczne */}
              <PictureAsset
                source={DIALOG_PERSON}
                ext="png"
                alt="egzamin"
                wrapperClassName="pointer-events-none absolute inset-0"
                className="h-full w-full object-cover object-bottom"
              />
            </div>
            <Content
              info={info}
              onOpen={openCalendar}
              onDone={() => handleOpenChange(false)}
            />
          </div>
          <DialogClose />
        </DialogContent>
      </Dialog>
      <PracticeExamAddToCalendarDialog
        isOpen={isAddToCalendarOpen}
        setOpen={handleCalendarOpenChange}
        dateToAdd={new Date(practiceExamStatus.startDate)}
        startTime={info.startTime}
        endTime={info.endTime}
      />
    </>
  );
};
